import React, { useState } from 'react';
import type { FormComponent } from '../../../types/builder';
import { Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface FormRendererProps {
  component: FormComponent;
}

export const FormRenderer: React.FC<FormRendererProps> = ({ component }) => {
  const { settings } = component;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get form data
      const formData = new FormData(e.currentTarget);
      const data: Record<string, string> = {};
      
      settings.fields.forEach((field) => {
        const value = formData.get(field.id);
        data[field.label] = value?.toString() || '';
      });

      // In Builder, just show success without actually submitting
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSuccess(true);
      toast.success('Form submitted successfully! (Preview mode)');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        e.currentTarget.reset();
      }, 3000);
    } catch (error) {
      toast.error('Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px 24px`,
        }}
      >
        <div className="max-w-md mx-auto bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-900 mb-2">Thank You!</h3>
          <p className="text-green-700">Your response has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        padding: `${settings.padding}px 24px`,
      }}
    >
      <div className="max-w-md mx-auto">
        <form onSubmit={handleSubmit} className="space-y-5">
          {settings.fields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  name={field.id}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  rows={4}
                />
              ) : (
                <input
                  type={field.type}
                  name={field.id}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              )}
            </div>
          ))}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{ backgroundColor: settings.submitButtonColor }}
            className="w-full text-white py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send size={20} />
                {settings.submitButtonText}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

